import React from 'react';
import { Rnd } from 'react-rnd';
import { clamp, getBlockDisplayText, snapValue, VARIABLE_SAMPLE_VALUES } from './builderModel.js';
import BlockSelectionOverlay from './BlockSelectionOverlay.jsx';

function isVisibleByRule(block) {
  const rule = String(block.visible_when || '').trim();
  if (!rule) return true;
  const same = Boolean(VARIABLE_SAMPLE_VALUES.is_same_credential);
  const map = {
    is_same_credential: same,
    show_single_code: same,
  };
  if (rule.startsWith('!')) {
    const key = rule.slice(1).trim();
    return key in map ? !map[key] : true;
  }
  return rule in map ? map[rule] : true;
}

function CanvasBlock({ block, selected, onSelect, onChange, page, sampleValue }) {
  if (block.visible === false || !isVisibleByRule(block)) return null;
  const isLocked = Boolean(block.locked);
  const borderColor = selected ? '#22d3ee' : 'rgba(148,163,184,0.45)';
  const borderWidth = Number(block.style?.borderWidth ?? 1);
  const fillColor = block.style?.fillColor ?? block.style?.backgroundColor ?? 'transparent';
  const opacity = Number(block.style?.opacity ?? 1);

  const shapeStyle = {
    border: `${Math.max(0, borderWidth)}px ${selected ? 'solid' : 'dashed'} ${selected ? borderColor : (block.style?.borderColor || borderColor)}`,
    background: fillColor,
    opacity,
  };

  if (block.type === 'triangle') {
    const direction = String(block.direction || 'right');
    const clipMap = {
      right: 'polygon(0 0, 100% 50%, 0 100%)',
      left: 'polygon(100% 0, 0 50%, 100% 100%)',
      up: 'polygon(50% 0, 0 100%, 100% 100%)',
      down: 'polygon(0 0, 100% 0, 50% 100%)',
    };
    shapeStyle.clipPath = clipMap[direction] || clipMap.right;
  }
  if (block.type === 'polygon_accent') {
    shapeStyle.clipPath = 'polygon(0 0, 100% 0, 86% 100%, 0 100%)';
  }
  if (block.type === 'circle_badge') {
    shapeStyle.borderRadius = '9999px';
  }
  if (block.type === 'line') {
    shapeStyle.border = 'none';
  }

  const renderContent = () => {
    if (block.type === 'line') {
      return <div style={{ width: '100%', height: '100%', background: fillColor }} />;
    }
    if (block.type === 'divider') {
      return <div style={{ width: '100%', borderBottom: `1px solid ${block.style?.borderColor || block.style?.color || '#111827'}` }} />;
    }
    if (block.type === 'qr_code') {
      return (
        <div className="flex h-full w-full items-center justify-center rounded border border-dashed border-slate-400/70 bg-white/90 text-xs text-slate-700">
          QR
        </div>
      );
    }
    if (block.type === 'logo' || block.type === 'image') {
      if (String(block.src || '').trim()) {
        return (
          <img
            src={String(block.src)}
            alt="voucher-logo"
            className="h-full w-full object-contain"
            draggable={false}
          />
        );
      }
      return (
        <div className="flex h-full w-full items-center justify-center rounded border border-dashed border-slate-400/70 bg-white/90 text-[10px] text-slate-700">
          Logo
        </div>
      );
    }
    if (['background_layer', 'rectangle', 'rounded_box', 'triangle', 'polygon_accent', 'box'].includes(block.type)) {
      return null;
    }
    if (block.type === 'footer_bar') {
      return <span style={{ width: '100%', whiteSpace: 'pre-wrap', color: block.style?.color || '#fff' }}>{sampleValue || getBlockDisplayText(block)}</span>;
    }
    if (block.type === 'circle_badge') {
      return <span style={{ width: '100%', textAlign: 'center', alignSelf: 'center', color: block.style?.color || '#fff' }}>{sampleValue || getBlockDisplayText(block)}</span>;
    }
    if (block.type === 'price_badge') {
      return <span style={{ width: '100%', textAlign: 'center', alignSelf: 'center', color: block.style?.color || '#111827' }}>{sampleValue || getBlockDisplayText(block)}</span>;
    }
    return <span style={{ width: '100%', whiteSpace: 'pre-wrap' }}>{sampleValue || getBlockDisplayText(block)}</span>;
  };

  return (
    <Rnd
      size={{ width: block.width, height: block.height }}
      position={{ x: block.x, y: block.y }}
      scale={Number(page.zoom || 1)}
      bounds="parent"
      disableDragging={isLocked}
      enableResizing={!isLocked}
      style={{ zIndex: Number(block.z_index || 1) }}
      onDragStart={() => onSelect(block.id)}
      onDragStop={(_, data) => {
        const nx = snapValue(data.x, page.snap_enabled, page.grid_size);
        const ny = snapValue(data.y, page.snap_enabled, page.grid_size);
        onChange(block.id, {
          x: clamp(nx, 0, Math.max(0, page.width - block.width)),
          y: clamp(ny, 0, Math.max(0, page.height - block.height)),
        });
      }}
      onResizeStart={() => onSelect(block.id)}
      onResizeStop={(_, __, ref, ___, position) => {
        const nw = snapValue(parseFloat(ref.style.width), page.snap_enabled, page.grid_size);
        const nh = snapValue(parseFloat(ref.style.height), page.snap_enabled, page.grid_size);
        const nx = snapValue(position.x, page.snap_enabled, page.grid_size);
        const ny = snapValue(position.y, page.snap_enabled, page.grid_size);
        onChange(block.id, {
          x: clamp(nx, 0, Math.max(0, page.width - nw)),
          y: clamp(ny, 0, Math.max(0, page.height - nh)),
          width: clamp(nw, 24, Math.max(24, page.width - nx)),
          height: clamp(nh, 18, Math.max(18, page.height - ny)),
        });
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id, { addToSelection: Boolean(e.metaKey || e.ctrlKey) });
      }}
    >
      <div
        className="relative h-full w-full overflow-hidden rounded"
        style={{
          ...shapeStyle,
          background: ['text', 'variable_text', 'voucher_code', 'username', 'password', 'profile_name', 'duration', 'quota', 'support_contact', 'custom_text', 'adaptive_credential'].includes(block.type)
            ? 'rgba(255,255,255,0.03)'
            : shapeStyle.background,
          color: block.style?.color || '#111827',
          fontSize: `${Number(block.style?.fontSize || 14)}px`,
          fontWeight: Number(block.style?.fontWeight || 500),
          textAlign: block.style?.textAlign || 'left',
          borderRadius: `${Number(block.style?.borderRadius || 0)}px`,
          padding: '6px',
          display: 'flex',
          alignItems: block.type === 'divider' ? 'flex-end' : 'flex-start',
          justifyContent: block.type === 'qr_code' ? 'center' : 'flex-start',
          userSelect: 'none',
          cursor: isLocked ? 'not-allowed' : 'move',
        }}
      >
        {renderContent()}
        {selected ? (
          <div className="pointer-events-none absolute right-1 top-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] text-cyan-200">
            {Math.round(Number(block.x || 0))},{Math.round(Number(block.y || 0))}
          </div>
        ) : null}
        <BlockSelectionOverlay visible={selected} />
      </div>
    </Rnd>
  );
}

export default function VoucherCanvas({
  state,
  selectedId,
  selectedIds = [],
  onSelect,
  onUpdateBlock,
  onCreateBlockAt,
}) {
  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-cyan-300/20 bg-[#061020]/70 p-3">
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-700/70 bg-[#020617] p-4">
        <div className="flex min-h-full items-start justify-center">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const blockIndex = e.dataTransfer.getData('application/x-voucher-block-index');
              const blockType = e.dataTransfer.getData('application/x-voucher-block-type');
              const rect = e.currentTarget.getBoundingClientRect();
              const zoom = Math.max(0.1, Number(state.page.zoom || 1));
              const x = (e.clientX - rect.left) / zoom;
              const y = (e.clientY - rect.top) / zoom;
              onCreateBlockAt(blockIndex || blockType, x, y);
            }}
            onClick={() => onSelect('')}
            style={{
              position: 'relative',
              width: `${state.page.width}px`,
              height: `${state.page.height}px`,
              transform: `scale(${Number(state.page.zoom || 1)})`,
              transformOrigin: 'top center',
              background: state.styles.background_color,
              color: state.styles.text_color,
              fontFamily: state.styles.font_family,
              boxShadow: '0 8px 28px rgba(2,6,23,0.45)',
              backgroundImage: state.page.grid_enabled
                ? `linear-gradient(to right, rgba(148,163,184,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.12) 1px, transparent 1px)`
                : 'none',
              backgroundSize: state.page.grid_enabled ? `${state.page.grid_size}px ${state.page.grid_size}px` : 'auto',
            }}
          >
            {state.blocks.length === 0 ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-8 text-center text-sm text-slate-500">
                Seret blok ke area desain atau klik salah satu blok untuk mulai menyusun template.
              </div>
            ) : null}

            {state.blocks
              .slice()
              .sort((a, b) => Number(a.z_index || 0) - Number(b.z_index || 0))
              .map((block) => (
                <CanvasBlock
                  key={block.id}
                  block={block}
                  selected={selectedId === block.id || selectedIds.includes(block.id)}
                  page={state.page}
                  onSelect={onSelect}
                  onChange={onUpdateBlock}
                  sampleValue={block.binding ? VARIABLE_SAMPLE_VALUES[block.binding] : ''}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
